<template>
    <Component
        :is="componentType"
        :to="getItemLink"
        :external="isExternalLink"
        class="table-cell-wrapper"
        :data-row-id="`${data.id}`"
        :data-testid="`project-list-table-td-${index}-${column}`"
        :class="[`${column}-column`]"
        :[`data-row-${column}`]="props.data[props.column]"
    >
        <template v-if="column === 'actions'">
            <TableActionColumn
                :actions="data.actions"
                :row-id="data.id"
                :type="data.type"
                :payment-ids="data.expensePaymentIds"
                :batch-ids="data.expenseBatchIds"
                :correlation-id="data.correlationId"
            />
        </template>
        <template v-else-if="column === 'status'">
            <TableStatusColumn :row-id="data.id" :status="data.status" :expiration-date="data.plannedExpirationDate" />
        </template>
        <template v-else-if="column === 'total'">
            <TableTotalColumn :row-id="data.id" :payment-limit-date="data.paymentLimitDate" :project="data" />
        </template>
        <template v-else-if="column === 'renewalStatus'">
            <TableRenewalStatusColumn
                :row-id="data.id"
                :renewal-status="data.renewalStatus"
                :renewal-target-correlation-id="data.renewalTargetCorrelationId"
            />
        </template>
        <div v-else-if="column === 'purchaseOrder'" class="table-cell-wrapper_content / flex" @click="handleShowDialog">
            <div v-if="data.purchaseOrderNumber" class="table-cell-wrapper_content_purchase-order">
                <div class="table-cell-wrapper_content_purchase-order_display / flex">
                    <div v-if="!isOverflowing" ref="cellContent" class="cellContent table-cell-wrapper_content_purchase-order_display_number">
                        {{ data.purchaseOrderNumber }}
                    </div>
                    <VJoyTooltip v-else variant="secondary" class="joy-tooltip" placement="bottom">
                        <div ref="cellContent" class="cellContent table-cell-wrapper_content_purchase-order_display_number">
                            {{ data.purchaseOrderNumber }}
                        </div>
                        <template #tooltip-content>
                            <p>{{ data.purchaseOrderNumber }}</p>
                        </template>
                    </VJoyTooltip>
                    <span class="table-cell-wrapper_content_purchase-order_display_amount">
                        {{ formatAmount(data.currency, data.purchaseOrderBudget ?? 0, {decimals: 2}) }}
                    </span>
                </div>
                <VJoyIconButton
                    v-if="hasAuthorityToOpenDialog"
                    class="table-cell-wrapper_content_purchase-order_edit-icon"
                    icon="pen"
                    variant="ghost"
                    size="xxsmall"
                />
            </div>
            <div v-else-if="hasAuthorityToOpenDialog" class="table-cell-wrapper_content_modify">
                <VJoyIconButton icon="add-circle" variant="ghost" size="xsmall" :label="$t('generic.form.modify.label')" />
            </div>
            <div v-else>{{ '-' }}</div>
        </div>
        <div v-else class="table-cell-wrapper_content / flex">
            <VJoyAvatar
                v-if="column === 'freelancer'"
                size="small"
                :photo-url="`${data.freelancerAvatar}?func=crop&w=30&h=30`"
                :full-name="data.freelancer"
            />
            <div v-if="!isOverflowing" ref="cellContent" class="cellContent">
                {{ getLabel }}
            </div>
            <VJoyTooltip v-else variant="secondary" class="joy-tooltip" placement="bottom">
                <div ref="cellContent" class="cellContent">
                    {{ getLabel }}
                </div>
                <template #tooltip-content>
                    <p>{{ getLabel }}</p>
                </template>
            </VJoyTooltip>
        </div>
    </Component>
    <PurchaseOrderDialog v-if="isDialogVisible" :visible="isDialogVisible" :data="data" @close:dialog="handleShowDialog" />
</template>

<script setup lang="ts">
    import {computed, ref, useTemplateRef, watch, type Ref} from 'vue';
    import {useUserStore} from '@common/stores';
    import TableRenewalStatusColumn from '@features/projects-list/ui/table/TableRenewalStatusColumn.vue';
    import TableTotalColumn from '@features/projects-list/ui/table/TableTotalColumn.vue';
    import {navigateTo, helpers as routehelpers, type NuxtRoute} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {NuxtLink} from '#components';
    import {useTranslation, useGuestHost, useFormat} from '#imports';
    import {useContentOverflow, useEventBus} from '@malt/utils';
    import {VJoyAvatar, VJoyIconButton, VJoyTooltip} from '@maltjoy/core-vue';
    import type {OpportunityType, RenewalStatus, VerticalType} from '@api';
    import {eventBus} from '~/common/events';
    import {ClientProjectType} from '~/openapi';
    import PurchaseOrderDialog from './PurchaseOrderDialog.vue';
    import TableActionColumn from './TableActionColumn.vue';
    import TableStatusColumn from './TableStatusColumn.vue';
    import type {FormattedClientProjectResource} from '../../types';

    const {t} = useTranslation();
    const host = useGuestHost();
    const userStore = useUserStore();
    const {roles} = storeToRefs(userStore);
    const cellContent = useTemplateRef('cellContent');

    const {formatAmount} = useFormat();

    const {isOverflowing, startObserving, stopObserving} = useContentOverflow({root: cellContent as Ref<HTMLElement | undefined>, immediate: false});

    watch(
        isOverflowing,
        () => {
            stopObserving();
            startObserving();
        },
        {immediate: true},
    );

    const props = defineProps<{
        column: keyof FormattedClientProjectResource;
        data: FormattedClientProjectResource;
        index: number;
    }>();

    const componentType = computed(() => {
        if (props.column === 'actions' || props.column === 'renewalStatus') {
            return 'div';
        } else if (props.column === 'purchaseOrder') {
            if (hasAuthorityToOpenDialog.value) {
                return 'div';
            }
            return NuxtLink;
        } else {
            return NuxtLink;
        }
    });

    const getLabel = computed(() => {
        const label = props.data[props.column];
        if (typeof label === 'string') {
            if (props.column === 'verticalType') {
                return t(`projectclient.projectlist.table.verticalType.value.${label as VerticalType}`);
            }
            if (props.column === 'opportunityType') {
                return t(`projectclient.projectlist.table.opportunityType.value.${label as OpportunityType}`);
            }
            return label;
        }
        return '';
    });

    const getItemLink = computed(() => {
        if (props.data.type === ClientProjectType.ProjectProposal) {
            return `${host}/project-client/quote/${props.data.id}`;
        } else {
            return routehelpers.path(`/project/${props.data.id}`) as NuxtRoute<any, any>;
        }
    });

    const isExternalLink = computed(() => {
        return props.data.type === ClientProjectType.ProjectProposal;
    });

    // handle purchaseOrder dialog
    const isDialogVisible = ref(false);

    const hasAuthorityToOpenDialog = computed(() => roles.value.includes('ROLE_PO_MANAGER'));

    function handleShowDialog() {
        if (!hasAuthorityToOpenDialog.value) return;
        isDialogVisible.value = !isDialogVisible.value;
    }

    useEventBus(eventBus, 'forceNavigateToFirstResult', (id) => {
        if (id === props.data.id) {
            navigateTo(getItemLink.value, {external: isExternalLink.value});
        }
    });
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .table-cell-wrapper {
        display: block;
        position: relative;
        max-width: 100%;
        width: 100%;
        cursor: pointer;
        height: 100%;
        padding: 0 var(--joy-core-spacing-4);

        &_content {
            height: 100%;
            align-items: center;

            .cellContent {
                max-width: 100%;
                cursor: pointer;
            }

            .joy-avatar__wrapper {
                flex: 0 0 auto;
            }

            .joy-tooltip,
            .joy-tooltip__wrapper {
                max-width: 100%;
                cursor: pointer;
            }

            &_purchase-order {
                height: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
                gap: var(--joy-core-spacing-2);

                .table-cell-wrapper_content_purchase-order_display {
                    flex-flow: column wrap;

                    &_number {
                        margin-bottom: 0;

                        @include text.getFontBySize(primary-300);

                        color: var(--joy-color-neutral-70);
                    }

                    &_amount {
                        color: var(--joy-color-neutral-40);
                    }
                }

                &_edit-icon {
                    visibility: hidden;
                }
            }
        }

        &.startDate-column,
        &.endDate-column,
        &.total-column {
            .table-cell-wrapper_content {
                justify-content: flex-end;
            }
        }

        &.projectOwner-column {
            table-layout: fixed;
        }

        &.freelancer-column {
            .table-cell-wrapper_content {
                flex-flow: nowrap;
                gap: var(--joy-core-spacing-2);
            }
        }

        &.organization-column,
        &.title-column,
        &.company-column,
        &.purchaseOrder-column {
            max-width: 280px;
        }

        &.freelancer-column {
            max-width: 160px;
        }

        &.projectOwner-column {
            max-width: 220px;
        }

        &:hover {
            .table-cell-wrapper_content_purchase-order_edit-icon {
                visibility: visible;
            }
        }
    }

    .joy-button {
        z-index: 0 !important;
    }
</style>
